/**
 * NotificationsTab - Notification preferences.
 * Per CONTEXT.md: Email + in-app toggles for team activity and listing events.
 */

import { useTranslation } from 'react-i18next';
import type { NotificationPreferences } from '../../types/company';

interface NotificationsTabProps {
  preferences: NotificationPreferences;
  onChange: (prefs: NotificationPreferences) => void;
  onSave: () => void;
  isSaving: boolean;
  canEdit: boolean;
}

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
        ${checked ? 'bg-[#004D40]' : 'bg-gray-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:ring-offset-2'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
          ${checked ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

export default function NotificationsTab({
  preferences,
  onChange,
  onSave,
  isSaving,
  canEdit,
}: NotificationsTabProps) {
  const { t } = useTranslation('companySettings');

  const handleToggle = (key: keyof NotificationPreferences) => (checked: boolean) => {
    onChange({ ...preferences, [key]: checked });
  };

  return (
    <div className="space-y-8">
      {/* Section header */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--pv-color-primary)]">{t('notifications.heading')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('notifications.description')}</p>
      </div>

      {/* Team Activity */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-base font-medium text-gray-900 mb-1">
          {t('notifications.teamActivity.title')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('notifications.teamActivity.description')}
        </p>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{t('notifications.teamActivity.email')}</span>
            <Toggle
              checked={preferences.emailTeamActivity}
              onChange={handleToggle('emailTeamActivity')}
              disabled={!canEdit}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{t('notifications.teamActivity.inApp')}</span>
            <Toggle
              checked={preferences.inAppTeamActivity}
              onChange={handleToggle('inAppTeamActivity')}
              disabled={!canEdit}
            />
          </div>
        </div>
      </div>

      {/* Listing Events */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-base font-medium text-gray-900 mb-1">
          {t('notifications.listingEvents.title')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('notifications.listingEvents.description')}
        </p>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{t('notifications.listingEvents.email')}</span>
            <Toggle
              checked={preferences.emailListingEvents}
              onChange={handleToggle('emailListingEvents')}
              disabled={!canEdit}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{t('notifications.listingEvents.inApp')}</span>
            <Toggle
              checked={preferences.inAppListingEvents}
              onChange={handleToggle('inAppListingEvents')}
              disabled={!canEdit}
            />
          </div>
        </div>
      </div>

      {/* Save button */}
      {canEdit && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onSave}
            disabled={isSaving}
            className="px-6 py-2 bg-[#004D40] text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? t('notifications.saving') : t('notifications.save')}
          </button>
        </div>
      )}
    </div>
  );
}
